import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import moment from 'moment'
import Path from '@leverege/path'

import { Button, Label, Selector } from '@leverege/ui-elements'
// eslint-disable-next-line 
import { TextInput, LabelGroup, Section, Tags, Text, Flex, InsetSection, JsonEditorDialog } from '~/views' 

import style from './style.css'

const PERMS = [ 
  { name : 'None', value : null }, 
  { name : 'Read Only', value : 'r' },
  { name : 'Read/Write', value : 'rw' } 
]
export default class BlueprintEditor extends React.Component {

  constructor( props ) {
    super( props )
    this.state = {
      showDialog : false,
    }
    this.pathToAnonPermissions = new Path( 'permissions/anonymous/access' )
    this.pathToUserPermissions = new Path( 'permissions/user/access' )
  }

  static propTypes = {
    blueprint : PropTypes.object, 
    currentTags : PropTypes.object,
    onChange : PropTypes.func.isRequired
  }

  changePath( path, value, err ) {
    const { onChange, blueprint } = this.props 
    if ( err ) {
      console.error( `Unable to set value ${value} at path ${path} due to error: ${err.message}` )
      return
    }
    onChange( { target : blueprint, path, value } );
  }

  render() {
    const { showDialog } = this.state 
    const { blueprint, small, currentTags } = this.props

    if ( blueprint == null ) {
      return <div />
    }

    if ( small ) {

      return (
        <Section title={blueprint.name} subtitle={`created ${moment( blueprint.created ).format( 'lll' )}`}>
          <Flex variant="vSpace" direction="column" align="stretch">

            <LabelGroup>
              <Label>Name</Label>
              <TextInput
                hint="Human Readable name"
                value={blueprint.name} 
                changeOnReturn 
                onChange={( v, err ) => this.changePath( 'name', v, err )} />
            </LabelGroup>
            <LabelGroup>
              <Label>Description</Label>
              <TextInput
                hint="Description of blueprint"
                value={blueprint.desc}
                changeOnReturn 
                onChange={( v, err ) => this.changePath( 'desc', v, err )} />
            </LabelGroup>
            <LabelGroup vertical>
              <Label>Tags</Label>
              <Tags
                tags={blueprint.tags}
                autofocus={false}
                suggestions={currentTags}
                tagAdded={( tag, arr ) => this.changePath( 'tags', arr, false )}
                tagRemoved={( tag, arr ) => this.changePath( 'tags', arr, false )} />
            </LabelGroup>
          </Flex>
        </Section>
      ) 
    }

    const aPerm = this.pathToAnonPermissions.get( blueprint, null )
    const uPerm = this.pathToUserPermissions.get( blueprint, null )

    if ( aPerm === 'wr' ) { aPerm === 'rw' }
    if ( uPerm === 'wr' ) { uPerm === 'rw' }

    let stringifiedMetadata
    if ( blueprint.metadata ) {
      stringifiedMetadata = JSON.stringify( blueprint.metadata, null, 2 )
    }

    return (
      <Flex variant="vSpace" direction="column" align="stretch">
          
        <LabelGroup>
          <Label>ID</Label>
          <Text type="dim" text={blueprint.id} />
        </LabelGroup>
        <LabelGroup>
          <Label>Purpose</Label>
          <Text type="dim" text={blueprint.purpose || 'device'} />
        </LabelGroup>
        <LabelGroup>
          <Label>Name</Label>
          <TextInput
            hint="Human readable name"
            value={blueprint.name} 
            changeOnReturn 
            onChange={( v, err ) => { this.changePath( 'name', v, err ) }} />
        </LabelGroup>
        <LabelGroup>
          <Label>Description</Label>
          <TextInput
            hint="Description of blueprint"
            value={blueprint.desc}
            changeOnReturn 
            onChange={( v, err ) => { this.changePath( 'desc', v, err ) }} />
        </LabelGroup>
        <LabelGroup>
          <Label>Msg Route</Label>
          <TextInput
            hint="Message Route ID..."
            value={blueprint.messageRouteId} 
            validate={aliasValidator}
            changeOnReturn 
            onChange={( v, err ) => { this.changePath( 'messageRouteId', v, err ) }} />
        </LabelGroup>
        {
          blueprint.messageRouteId ? 
            (
              <LabelGroup>
                <Label />
                <Link to={`/project/${blueprint.projectId}/messageRoute/${blueprint.messageRouteId}`}>
                  View Message Route<i className="fa fa-arrow-right" style={{ marginLeft : '8px' }} />
                </Link>
              </LabelGroup>
            ) :
            null
        }
        <LabelGroup>
          <Label>Alias</Label>
          <TextInput
            hint="The interface type name (use camelCase, no spaces or special characters)"
            value={blueprint.alias} 
            validate={aliasValidator}
            acceptKey={aliasKeyValidator}
            changeOnReturn 
            onChange={( v, err ) => { this.changePath( 'alias', v, err ) }} />
        </LabelGroup>
        <LabelGroup>
          <Label />
          <Text type="dim" text="This is used when accessing data via rest interface" />
        </LabelGroup>
        <LabelGroup>
          <Label>Tags</Label>
          <Tags
            tags={blueprint.tags}
            autofocus={false}
            suggestions={currentTags}
            tagAdded={( tag, arr ) => { this.changePath( 'tags', arr, false ) }}
            tagRemoved={( tag, arr ) => { this.changePath( 'tags', arr, false ) }} />
        </LabelGroup>

        <Label>Permissions</Label>
        <LabelGroup>
          <Label>Anonymous</Label>
          <Selector
            style={{ flex : '0 0 150px' }}
            values={PERMS}
            value={aPerm}
            onChange={( value ) => { this.changePath( 'permissions/anonymous/access', value.value, false ); }} />
        </LabelGroup>
        <LabelGroup>
          <Label>User</Label>
          <Selector
            style={{ flex : '0 0 150px' }}
            values={PERMS}
            value={uPerm}
            onChange={( value ) => { this.changePath( 'permissions/user/access', value.value, false ); }} />
        </LabelGroup>
                  
        <LabelGroup vertical>
          <Flex direction="row">
            <Label style={{ marginRight : 0 }} >Metadata</Label>
            <Button variant="secondarySmall" onClick={() => this.setState( { showDialog : true } )}>{stringifiedMetadata ? 'Edit' : 'Add'}</Button>
          </Flex>
          <InsetSection style={{ width : '100%' }}>
            <div className={stringifiedMetadata ? style.metadata : style.noMetadata}>{stringifiedMetadata || 'No Metadata' }</div> 
          </InsetSection>
        </LabelGroup>

        <br />

        <LabelGroup>
          <Label />
          <Text type="dim" text={`Created on ${moment( blueprint.created ).format( 'lll' )}`} />
        </LabelGroup>

        <JsonEditorDialog 
          title="Blueprint Metadata"
          message="Enter the JSON object for the Blueprint metadata"
          show={showDialog} 
          onOk={( metadata ) => { 
            this.changePath( 'metadata', metadata, false )
            this.setState( { showDialog : false } ) 
          }}
          onCancel={() => { this.setState( { showDialog : false } ) }}
          value={blueprint.metadata} />

      </Flex>
    )
  }
}

function aliasValidator( value ) {
  if ( value.length > 500 || !/^[a-zA-Z0-9_\-]+$/.test( value ) ) {
    return false
  }
  return true
}

function aliasKeyValidator( evt ) {
  const inp = String.fromCharCode( evt.charCode )
  return /[a-zA-Z0-9_\-]/.test( inp )
}
